import { FC, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Flex, Icon, Link, Stack, Text } from '@chakra-ui/react';

import { IconDocumentAdd } from '../assets';

type BulkDropzoneProps = {
  label?: string;
  name?: string;
  setFiles: (value: File[]) => void;
  maxFiles?: number;
};

export const BulkDropzone: FC<BulkDropzoneProps> = ({
  label,
  name,
  setFiles,
  maxFiles = 20,
}) => {
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        setFiles(acceptedFiles);
      }
    },
    [setFiles],
  );

  const { getInputProps, getRootProps, isDragActive } = useDropzone({
    onDrop,
    maxFiles, // limiting the number of files that can be dropped
  });

  return (
    <Stack gap={8}>
      {maxFiles > 1 && (
        <Text fontSize="xl" fontWeight="medium">
          Bulk document upload
        </Text>
      )}
      <Flex
        alignItems="center"
        bg="gray.25"
        borderColor="gray.200"
        borderStyle="dashed"
        borderWidth="1px"
        justifyContent="center"
        minH="175px"
        py={12}
        w="full"
        {...getRootProps()}
      >
        {/* the file input is the control the drop area stands for, so it
            takes the field's name as its id and the field's label as its own */}
        <input {...getInputProps({ 'aria-label': label, id: name })} />

        {isDragActive ? (
          <Text>Drop file(s) here</Text>
        ) : (
          <Stack gap={2}>
            <Flex alignItems="center" justifyContent="center">
              <Icon as={IconDocumentAdd} boxSize={6} mr={4} />

              <Text>
                Drop your file{maxFiles > 1 && `s`} here or{' '}
                <Link color="primary.500" textDecoration="underline">
                  browse
                </Link>
              </Text>
            </Flex>
          </Stack>
        )}
      </Flex>
    </Stack>
  );
};
